<template>
    <b-card
        no-body
        :border-variant="borderVariant"
        class="unclassified-verification"
    >
        <DocumentPreview
            ref="preview"
            :product="product"
            :file="previewFile"
        />
        <ReviewProcessModal
            :visible="showReviewProcessModal"
            :process-id="selectedReviewProcessId"
            @close="closeReviewProcessModal"
            @completed="onReviewProcessCompleted"
        />
        <div class="card-header pr-2">
            <b-card-title>{{ $t('Component(UnclassifiedVerification):title') }}</b-card-title>
            <div class="demo-inline-spacing">
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="btn-icon mt-0 mr-0"
                    :disabled="loading || uploading"
                    @click="fetchDocuments"
                >
                    <b-spinner
                        v-if="loading"
                        small
                    />
                    <feather-icon
                        v-else
                        icon="RefreshCwIcon"
                    />
                </b-button>
            </div>
        </div>

        <Overlay :show="showOverlay">
            <div class="px-2">
                <b-alert
                    v-if="error"
                    variant="danger"
                    show
                >
                    <div class="alert-body">
                        {{ error }}
                    </div>
                </b-alert>

                <b-form-group :label="$t('Component(UnclassifiedVerification):upload-pdf-documents')">
                    <b-form-file
                        v-model="selectedFiles"
                        :placeholder="$t('Component(Placeholder):choose-file-or-drop')"
                        :drop-placeholder="$t('Component(UnclassifiedVerification):drop-files-here')"
                        accept="application/pdf,application/x-pdf"
                        multiple
                        :disabled="uploading"
                        @input="onSelectedFilesChange"
                    />
                </b-form-group>

                <div
                    v-if="queuedFiles.length"
                    class="selected-files mb-1"
                >
                    <b-badge
                        v-for="file in queuedFiles"
                        :key="file.name + file.size + file.lastModified"
                        variant="light-secondary"
                        class="mr-50 mb-50"
                    >
                        {{ file.name }}
                    </b-badge>
                </div>

                <div
                    v-if="queuedFiles.length"
                    class="text-right mb-2"
                >
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="secondary"
                        class="mr-1"
                        :disabled="uploading"
                        @click="clearSelection"
                    >
                        {{ $t('Component(Button):cancel') }}
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        :disabled="uploading"
                        @click="upload"
                    >
                        <b-spinner
                            v-if="uploading"
                            small
                        />
                        <span v-else>{{ $t('Component(Button):upload') }}</span>
                    </b-button>
                </div>

                <div
                    v-if="documents.length"
                    class="table-responsive"
                >
                    <b-table
                        :fields="fields"
                        :items="documents"
                        responsive="sm"
                        hover
                    >
                        <template #cell(document)="data">
                            <ShimmerText :active="isAnalysisPending(data.item)">
                                {{ getFileName(data.item) }}
                            </ShimmerText>
                        </template>

                        <template #cell(status)="data">
                            <b-badge :variant="getStatusVariant(getProcessingStatus(data.item))">
                                {{ getStatusLabel(getProcessingStatus(data.item)) }}
                            </b-badge>
                        </template>

                        <template #cell(updatedAt)="data">
                            {{ getUpdatedAt(data.item) }}
                        </template>

                        <template #cell(actions)="data">
                            <div
                                class="float-right"
                                style="min-width: 176px;"
                            >
                                <Button
                                    v-if="isReviewPending(data.item)"
                                    class="btn-sm mr-1"
                                    :disabled="!canOpenReview(data.item)"
                                    @click="openReviewProcess(data.item)"
                                >
                                    {{ $t('Component(Button):review') }}
                                </Button>
                                <Button
                                    class="b-icon btn-sm mr-1"
                                    @click="download(data.item)"
                                >
                                    <b-spinner
                                        v-if="isDocumentDownloading(data.item)"
                                        small
                                    />
                                    <feather-icon
                                        v-else
                                        icon="DownloadCloudIcon"
                                    />
                                </Button>
                                <Button
                                    class="b-icon btn-sm"
                                    @click="previewDocument(data.item)"
                                >
                                    <feather-icon icon="EyeIcon" />
                                </Button>
                            </div>
                        </template>
                    </b-table>
                </div>
            </div>
        </Overlay>
    </b-card>
</template>

<script>
import {
    BAlert, BBadge, BButton, BCard, BCardTitle, BFormFile, BFormGroup, BSpinner, BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ProductApi } from '@core/api'
import Button from '@core/components/common/Button.vue'
import DownloadProductDocument from '@core/mixins/download-product-document'
import dateRenderer from '@core/mixins/date-renderer'
import Overlay from '@core/components/common/Overlay.vue'
import ShimmerText from '@core/components/common/ShimmerText.vue'
import { DocumentPreview } from '@core/components/product'
import { ReviewProcessModal } from '@/views/research/review/components'

const ProcessingStatus = {
    CREATED: 'created',
    ANALYSIS_PENDING: 'analysis_pending',
    ANALYSIS_COMPLETED: 'analysis_completed',
    REVIEW_PENDING: 'review_pending',
    COMPLETED: 'completed',
    FAILED: 'failed',
}

const analysisProcessingStatuses = [
    ProcessingStatus.CREATED,
    ProcessingStatus.ANALYSIS_PENDING,
    ProcessingStatus.ANALYSIS_COMPLETED,
]

export default {
    components: {
        BAlert,
        BBadge,
        BButton,
        BCard,
        BCardTitle,
        BFormFile,
        BFormGroup,
        BSpinner,
        BTable,
        Button,
        DocumentPreview,
        Overlay,
        ReviewProcessModal,
        ShimmerText,
    },
    directives: {
        Ripple,
    },
    mixins: [DownloadProductDocument, dateRenderer],
    props: {
        id: {
            type: [Number, String],
            required: true,
        },
        product: {
            type: Object,
            default() {
                return {}
            },
        },
        pollInterval: {
            type: Number,
            default: 5000,
        },
        autoLoad: {
            type: Boolean,
            default: true,
        },
        enablePolling: {
            type: Boolean,
            default: false,
        },
        documentSpace: {
            type: String,
            default: 'organization',
            validator(value) {
                return ['organization', 'supplier'].includes(value)
            },
        },
        reviewCompletionRefreshDelay: {
            type: Number,
            default: 2000,
        },
    },
    data() {
        return {
            documents: [],
            selectedFiles: [],
            queuedFiles: [],
            loading: false,
            uploading: false,
            waitingReviewCompletionRefresh: false,
            downloadingDocuments: {},
            error: null,
            pollTimer: null,
            reviewRefreshTimer: null,
            previewFile: null,
            showReviewProcessModal: false,
            selectedReviewProcessId: null,
            fields: [
                { key: 'document', label: this.$t('Component(Grid):column-name') },
                { key: 'status', label: this.$t('Component(Grid):column-status') },
                { key: 'updatedAt', label: this.$t('Component(Label):updated-at') },
                { key: 'actions', label: '' },
            ],
        }
    },
    computed: {
        borderVariant() {
            return this.documentSpace === 'supplier' ? 'primary' : 'light'
        },
        shouldPoll() {
            return this.enablePolling && this.documents.some(document => this.shouldPollDocument(document))
        },
        showOverlay() {
            return this.uploading || this.waitingReviewCompletionRefresh
        },
    },
    watch: {
        enablePolling() {
            this.syncPolling()
        },
    },
    created() {
        if (this.autoLoad) {
            this.fetchDocuments()
        }
    },
    beforeDestroy() {
        this.stopPolling()
        this.clearReviewRefreshTimer()
    },
    methods: {
        fetchDocuments() {
            this.loading = true
            this.error = null

            return this
                .getUnclassifiedVerificationDocuments()
                .then(r => {
                    this.documents = r.data
                    this.syncPolling()
                })
                .catch(error => {
                    this.error = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        upload() {
            if (!this.queuedFiles.length) {
                return
            }

            this.uploading = true
            this.error = null

            this
                .uploadUnclassifiedVerificationDocuments(this.queuedFiles)
                .then(() => {
                    this.clearSelection()
                    this.$emit('uploaded')
                    this.$emit('refreshDocuments')
                    return this.fetchDocuments()
                })
                .catch(error => {
                    this.error = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.uploading = false
                })
        },
        onSelectedFilesChange(files) {
            if (!files) {
                this.queuedFiles = []
                return
            }

            this.queuedFiles = Array.isArray(files) ? files : [files]
        },
        clearSelection() {
            this.selectedFiles = []
            this.queuedFiles = []
        },
        getUnclassifiedVerificationDocuments() {
            if (this.documentSpace === 'supplier') {
                return ProductApi.getSupplierUnclassifiedVerificationDocuments(this.id)
            }

            return ProductApi.getOrganizationUnclassifiedDocuments(this.id)
        },
        uploadUnclassifiedVerificationDocuments(files) {
            if (this.documentSpace === 'supplier') {
                return ProductApi.uploadSupplierUnclassifiedVerificationDocuments(this.id, files)
            }

            return ProductApi.uploadOrganizationUnclassifiedDocuments(this.id, files)
        },
        syncPolling() {
            if (!this.shouldPoll) {
                this.stopPolling()
                return
            }

            this.startPolling()
        },
        startPolling() {
            if (!this.enablePolling || this.pollTimer || !this.pollInterval) {
                return
            }

            this.pollTimer = window.setInterval(() => {
                this.fetchDocuments()
            }, this.pollInterval)
        },
        stopPolling() {
            if (!this.pollTimer) {
                return
            }

            window.clearInterval(this.pollTimer)
            this.pollTimer = null
        },
        shouldPollDocument(document) {
            const status = this.getProcessingStatus(document)

            if (analysisProcessingStatuses.includes(status)) {
                return true
            }

            return status === ProcessingStatus.REVIEW_PENDING && !this.getReviewProcessId(document)
        },
        getFileName(document) {
            return document.file && document.file.name ? document.file.name : ''
        },
        getProcessingStatus(document) {
            return document.processing && document.processing.status ? document.processing.status : ProcessingStatus.CREATED
        },
        getUpdatedAt(document) {
            return document.processing && document.processing.updatedAt ? this.renderDateTime(document.processing.updatedAt) : ''
        },
        getReviewProcessId(document) {
            return document.processing && document.processing.reviewProcessId ? document.processing.reviewProcessId : null
        },
        isReviewPending(document) {
            return this.getProcessingStatus(document) === ProcessingStatus.REVIEW_PENDING
        },
        isAnalysisPending(document) {
            return this.getProcessingStatus(document) === ProcessingStatus.ANALYSIS_PENDING
        },
        canOpenReview(document) {
            return !!this.getReviewProcessId(document)
        },
        openReviewProcess(document) {
            const processId = this.getReviewProcessId(document)
            if (!processId) {
                return
            }

            this.selectedReviewProcessId = processId
            this.showReviewProcessModal = true
        },
        closeReviewProcessModal() {
            this.showReviewProcessModal = false
            this.selectedReviewProcessId = null
        },
        onReviewProcessCompleted() {
            this.closeReviewProcessModal()
            this.scheduleReviewCompletionRefresh()
        },
        scheduleReviewCompletionRefresh() {
            this.clearReviewRefreshTimer()
            this.waitingReviewCompletionRefresh = true
            this.reviewRefreshTimer = window.setTimeout(() => {
                this.fetchDocuments()
                this.$emit('refreshDocuments')
                this.reviewRefreshTimer = null
                this.waitingReviewCompletionRefresh = false
            }, this.reviewCompletionRefreshDelay)
        },
        clearReviewRefreshTimer() {
            if (!this.reviewRefreshTimer) {
                return
            }

            window.clearTimeout(this.reviewRefreshTimer)
            this.reviewRefreshTimer = null
            this.waitingReviewCompletionRefresh = false
        },
        getStatusVariant(status) {
            switch (status) {
            case ProcessingStatus.CREATED:
                return 'secondary'
            case ProcessingStatus.ANALYSIS_PENDING:
                return 'warning'
            case ProcessingStatus.ANALYSIS_COMPLETED:
                return 'info'
            case ProcessingStatus.REVIEW_PENDING:
                return 'primary'
            case ProcessingStatus.COMPLETED:
                return 'success'
            case ProcessingStatus.FAILED:
                return 'danger'
            default:
                return 'secondary'
            }
        },
        getStatusLabel(status) {
            return this.$t(`Component(ProductDocumentProcessingStatusEnum):${status.replace(/_/g, '-')}`)
        },
        download(document) {
            this.$set(this.downloadingDocuments, document.id, true)
            this.downloadProductDocument(this.id, document)
                .finally(() => {
                    this.$set(this.downloadingDocuments, document.id, false)
                })
        },
        isDocumentDownloading(document) {
            return !!this.downloadingDocuments[document.id]
        },
        previewDocument(document) {
            this.previewFile = document
            this.$nextTick(() => {
                this.$refs.preview.show()
            })
        },
        getErrorMessage(error) {
            if (error && error.response && error.response.data) {
                return error.response.data.title || error.response.data.detail || this.$t('Component(Error):default')
            }

            return error && error.message ? error.message : this.$t('Component(Error):default')
        },
    },
}
</script>
